import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { Tile as TileT, BadgeSource } from '../../lib/home-schema.js';
import { TILE_COLORS } from '../../lib/tile-colors.js';

interface TileProps {
  tile: TileT;
  badgeValue?: number;
  editing?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
}

export function Tile({ tile, badgeValue, editing, onEdit, onRemove }: TileProps) {
  const color = TILE_COLORS[tile.color] ?? TILE_COLORS.slate;
  const badge = resolveBadge(tile, badgeValue);

  const tileBody = (
    <div className="flex w-full flex-col items-center text-center">
      <div
        className={clsx(
          'relative grid h-14 w-14 place-items-center overflow-hidden rounded-xl shadow-sm ring-1',
          tile.logoUrl ? 'bg-white ring-stone-300' : `${color.bg} ${color.fg} ${color.ring}`,
        )}
      >
        {tile.logoUrl ? (
          <img src={tile.logoUrl} alt={tile.label} className="h-full w-full object-cover" />
        ) : (
          <span className="text-2xl leading-none font-semibold tracking-tight">
            {tile.glyph ?? glyphFromLabel(tile.label)}
          </span>
        )}
        {badge !== null && badge > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-[#9C4A3A] px-1 text-[10px] font-bold text-white ring-2 ring-[#FFFFFF]">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <div className="mt-2 line-clamp-2 text-[11px] leading-tight text-stone-700 font-medium">
        {tile.label}
      </div>
    </div>
  );

  if (editing) {
    return (
      <div className="group relative">
        <button
          type="button"
          className="block w-full focus:outline-none"
          onClick={onEdit}
          aria-label={`Edit ${tile.label}`}
        >
          {tileBody}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full bg-stone-900 text-[10px] text-white shadow group-hover:flex"
          aria-label={`Remove ${tile.label}`}
        >
          ×
        </button>
      </div>
    );
  }

  if (tile.external) {
    return (
      <a
        href={tile.href}
        target="_blank"
        rel="noreferrer"
        className="block w-20 transition hover:-translate-y-0.5"
      >
        {tileBody}
      </a>
    );
  }

  return (
    <Link to={tile.href} className="block w-20 transition hover:-translate-y-0.5">
      {tileBody}
    </Link>
  );
}

function glyphFromLabel(label: string): string {
  const cleaned = label.replace(/[^A-Za-z0-9]/g, '');
  if (!cleaned) return '◆';
  return cleaned.slice(0, 2).toUpperCase();
}

function resolveBadge(tile: TileT, externalValue?: number): number | null {
  if (!tile.badge) return null;
  if (tile.badge.kind === 'static') return tile.badge.value;
  return externalValue ?? 0;
}

export function emptyTileBadge(_: BadgeSource): null {
  return null;
}
